import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Maximize2, Minimize2, Navigation, Radio, AlertCircle } from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';

export default function GPSPage() {
  const { sensorData, geoStatus } = useMQTT();
  const [fullscreen, setFullscreen] = useState(false);
  const gps = sensorData.gps;

  const hasFix =
    gps && Number.isFinite(gps.latitude) && Number.isFinite(gps.longitude) &&
    (gps.latitude !== 0 || gps.longitude !== 0);

  const span = useMemo(() => {
    const accDeg = gps?.accuracy ? (gps.accuracy / 111000) * 4 : 0.02;
    return Math.min(Math.max(accDeg, 0.0025), 0.1);
  }, [gps]);

  const mapUrl = useMemo(() => {
    if (!hasFix) return null;
    const bbox = [gps.longitude - span, gps.latitude - span,
                  gps.longitude + span, gps.latitude + span].join(',');
    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${gps.latitude},${gps.longitude}`;
  }, [hasFix, gps, span]);

  const statusLabel = ({
    live: 'Live tracking',
    locating: 'Acquiring GPS…',
    denied: 'Permission denied',
    unavailable: 'Unavailable',
    error: 'Error'
  })[geoStatus] || 'Idle';

  const statusColor =
    geoStatus === 'live'     ? '#10b981' :
    geoStatus === 'locating' ? '#f59e0b' : '#ef4444';

  const speedKmh =
    Number.isFinite(gps.speed) ? gps.speed * 3.6 : null;

  return (
    <div className={`grid gap-4 ${fullscreen ? 'fixed inset-0 z-[60] p-4 bg-black/80 backdrop-blur-xl' : ''}`}>
      <motion.div layout className="card-surface p-5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white shadow-glow">
          <MapPin className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold tracking-tight">GPS Tracking</h2>
          <p className="text-xs opacity-70">
            {gps.updatedAt ? `Updated ${new Date(gps.updatedAt).toLocaleTimeString()}` : 'Awaiting first fix…'}
          </p>
        </div>
        <span className="chip" style={{ background: `${statusColor}22`, color: statusColor }}>
          {geoStatus === 'live' ? <Radio className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
          {statusLabel}
        </span>
        <button
          onClick={() => setFullscreen(f => !f)}
          className="w-10 h-10 grid place-items-center rounded-xl hover:bg-black/5 dark:hover:bg-white/10"
        >
          {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
        </button>
      </motion.div>

      {/* Coord pills */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="card-surface p-3 text-center">
          <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Lat</p>
          <p className="text-lg font-extrabold tabular-nums mt-1">{hasFix ? gps.latitude.toFixed(5) : '—'}</p>
        </div>
        <div className="card-surface p-3 text-center">
          <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Lon</p>
          <p className="text-lg font-extrabold tabular-nums mt-1">{hasFix ? gps.longitude.toFixed(5) : '—'}</p>
        </div>
        <div className="card-surface p-3 text-center">
          <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Accuracy</p>
          <p className="text-lg font-extrabold tabular-nums mt-1">{gps.accuracy ? `±${gps.accuracy.toFixed(0)} m` : '—'}</p>
        </div>
        <div className="card-surface p-3 text-center">
          <p className="text-[10px] uppercase opacity-60 font-bold tracking-widest">Speed</p>
          <p className="text-lg font-extrabold tabular-nums mt-1">{speedKmh != null && speedKmh > 0.5 ? `${speedKmh.toFixed(1)} km/h` : '—'}</p>
        </div>
      </div>

      <motion.div layout className="card-surface overflow-hidden">
        <div className={`relative w-full ${fullscreen ? 'h-[70vh]' : 'h-[360px] sm:h-[480px]'}`}>
          {mapUrl ? (
            <iframe
              title="container-location"
              src={mapUrl}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3 opacity-60">
              <MapPin className="w-10 h-10" />
              <p className="font-bold">
                {geoStatus === 'denied'
                  ? 'Allow location to enable live tracking'
                  : geoStatus === 'locating'
                    ? 'Acquiring your location…'
                    : 'No fix yet'}
              </p>
            </div>
          )}
          {hasFix && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none">
              <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 grid place-items-center text-white shadow-glow animate-float">
                <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-pulse-soft" />
                <Navigation
                  className="relative w-6 h-6"
                  style={{
                    transform: Number.isFinite(gps.heading) ? `rotate(${gps.heading}deg)` : undefined,
                    transition: 'transform 0.4s ease'
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
